import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { EventCard as EventCardData } from "../api/events";
import { setRsvp } from "../api/events";
import { getSession } from "../api/client";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { FormatMascot } from "../components/FormatMascot";
import { Lightbox } from "../components/Lightbox";
import { TranslatableText } from "../components/TranslatableText";
import { formatStartsAt } from "../lib/format";
import { formatIcon } from "../lib/labels";
import type { City, District } from "@nifgash/shared/types";
import { tg } from "../lib/telegram";

type Props = {
  event: EventCardData;
  onUpdate: (e: EventCardData) => void;
};

type AccentMap = Record<string, "yellow" | "blue" | "purple" | "red" | "green">;
const FORMAT_ACCENT: AccentMap = {
  cafe: "yellow",
  run: "blue",
  boardgames: "purple",
  movie: "red",
  other: "green",
};

export function EventCardView({ event, onUpdate }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isAuthed = !!getSession();
  const isGoing = event.my_status === "going";
  const full = event.capacity != null && event.going_count >= event.capacity && !isGoing;
  const accent: "yellow" | "blue" | "purple" | "red" | "green" =
    (event.format ? FORMAT_ACCENT[event.format] : undefined) ?? "purple";

  async function toggle() {
    if (!isAuthed) {
      navigate("/login", { state: { from: "/events" } });
      return;
    }
    if (busy) return;
    setBusy(true);
    const prev = { ...event };
    const newStatus: "going" | "cancelled" = isGoing ? "cancelled" : "going";
    onUpdate({
      ...event,
      my_status: newStatus,
      going_count: event.going_count + (isGoing ? -1 : 1),
    });
    try {
      const res = await setRsvp(event.id, !isGoing);
      onUpdate({ ...event, my_status: res.status, going_count: res.going_count });
      tg()?.HapticFeedback?.impactOccurred("light");
    } catch (e) {
      onUpdate(prev);
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("409") && msg.includes("full")) alert(t("eventCard.alertFull"));
      else if (msg.includes("409")) alert(t("eventCard.alertCancelled"));
      else alert(t("eventCard.alertGenericError"));
    } finally {
      setBusy(false);
    }
  }

  const icon = event.format ? formatIcon[event.format as keyof typeof formatIcon] ?? "✨" : "✨";
  // Known slugs translate; custom free-text formats fall back to themselves.
  const fmt = event.format ? t(`labels.format.${event.format}`, { defaultValue: event.format }) : null;
  const districtText = event.district ? t(`labels.district.${event.district as District}`) : null;
  const cityText = event.city ? t(`labels.city.${event.city as City}`) : null;
  const locationSuffix = districtText ?? cityText;
  const venueLine = `${event.venue_name}${locationSuffix ? ", " + locationSuffix : ""}`;

  function openMaps() {
    // Prefer exact coordinates (lands on the precise pin) and fall back to a
    // text query when an event has no coords. Open via Telegram's in-app
    // handler when inside the Mini App, otherwise a normal new browser tab —
    // the previous code only called tg()?.openLink, which is a no-op on the
    // public website and made the address look unclickable.
    const e = event as typeof event & { lat: number | null; lng: number | null };
    const query =
      e.lat != null && e.lng != null
        ? `${e.lat},${e.lng}`
        : [event.venue_name, event.venue_address, districtText, cityText, "Israel"]
            .filter(Boolean)
            .join(", ");
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    const app = tg();
    app?.HapticFeedback?.impactOccurred("light");
    if (app?.openLink) app.openLink(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  function share() {
    const text = [
      `${icon} ${event.title}`,
      formatStartsAt(event.starts_at),
      venueLine,
    ].join("\n");
    const url = "https://t.me/NifgashHaifabot/app";
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    tg()?.HapticFeedback?.impactOccurred("light");
    tg()?.openTelegramLink(shareUrl);
  }

  return (
    <Card accent={accent}>
      <Lightbox src={lightboxOpen ? event.image_url : null} onClose={() => setLightboxOpen(false)} />
      <div className="p-4 pl-5">
        {/* Header: avatar circle (photo if any, else format emoji) + title + share */}
        <div className="flex items-start gap-3 mb-3">
          {event.image_url ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxOpen(true);
              }}
              className="flex-shrink-0 w-11 h-11 rounded-full overflow-hidden border border-ink-100 active:scale-90 transition-transform"
              aria-label={event.title}
            >
              <img src={event.image_url} alt="" className="w-full h-full object-cover" />
            </button>
          ) : (
            <div className="flex-shrink-0 w-11 h-11 rounded-full overflow-hidden">
              <FormatMascot format={event.format} size={44} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <TranslatableText
              as="div"
              text={event.title}
              className="text-[17px] font-bold text-ink-900 leading-tight"
            />
            {fmt && (
              <div className={`text-[12px] font-bold uppercase tracking-wider mt-0.5 text-brand-${accent}`}>
                {fmt}
              </div>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); share(); }}
            aria-label={t("eventCard.share")}
            className="flex-shrink-0 -mr-1 -mt-1 p-2 text-ink-400 hover:text-ink-700 active:scale-90 transition-all"
          >
            <ShareIcon />
          </button>
        </div>

        {/* Meta rows */}
        <div className="space-y-1.5 mb-4">
          {/* Venue — tappable, opens Google Maps */}
          <button
            onClick={(e) => { e.stopPropagation(); openMaps(); }}
            className="flex items-center gap-2 text-[14px] text-ink-700 w-full text-left active:opacity-60 transition-opacity"
          >
            <span className="text-brand-purple flex-shrink-0">
              <PinIcon />
            </span>
            <span className="leading-tight flex-1 min-w-0 truncate">{venueLine}</span>
            <span className="text-brand-purple flex-shrink-0">
              <ExternalIcon />
            </span>
          </button>
          <MetaRow icon={<CalIcon />} text={formatStartsAt(event.starts_at)} />
          <AttendeesRow event={event} />
        </div>

        {/* Description */}
        {event.description && (
          <div className="mb-4">
            <TranslatableText
              as="div"
              text={event.description}
              className="text-[14px] text-ink-700 leading-relaxed whitespace-pre-wrap"
            />
          </div>
        )}

        {/* RSVP button */}
        <Button
          stretched
          size="md"
          variant={isGoing ? "secondary" : "primary"}
          disabled={busy || full}
          onClick={(e) => { e.stopPropagation(); void toggle(); }}
        >
          {!isAuthed
            ? t("eventCard.signInToRsvp")
            : busy ? "…"
            : full ? t("eventCard.full")
            : isGoing ? t("eventCard.notGoing")
            : t("eventCard.imGoing")}
        </Button>
      </div>
    </Card>
  );
}

function AttendeesRow({ event }: { event: EventCardData }) {
  const { t } = useTranslation();
  const count = event.going_count;
  const cap = event.capacity;
  const countText = cap != null
    ? `${count} / ${cap}`
    : count === 0
      ? t("eventCard.nobodyYet")
      : t("eventCard.going", { count });

  const attendees = event.attendees ?? [];
  if (attendees.length === 0) {
    return (
      <div className="flex items-center gap-2 text-[14px] text-ink-700">
        <span className="text-ink-400 flex-shrink-0">
          <UsersIcon />
        </span>
        <span className="leading-tight">{countText}</span>
      </div>
    );
  }

  const visible = attendees.slice(0, 4);
  const rest = count - visible.length;
  return (
    <div className="flex items-center gap-2 text-[14px] text-ink-700">
      <div className="flex -space-x-2 flex-shrink-0">
        {visible.map((a) => (
          <div key={a.user_id} className="ring-2 ring-surface rounded-full">
            <Avatar url={a.photo_url} name={a.full_name} size={28} />
          </div>
        ))}
      </div>
      <span className="leading-tight flex-1 min-w-0 truncate">
        {visible.map((a) => firstWord(a.full_name)).filter(Boolean).join(", ")}
        {rest > 0 ? ` ${t("eventCard.andMore", { count: rest })}` : ""}
      </span>
    </div>
  );
}

function firstWord(name: string | null): string {
  if (!name) return "";
  return name.split(/\s+/)[0] ?? "";
}

function MetaRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 text-[14px] text-ink-700">
      <span className="text-ink-400 flex-shrink-0">{icon}</span>
      <span className="leading-tight">{text}</span>
    </div>
  );
}

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M12 22s8-7 8-13a8 8 0 0 0-16 0c0 6 8 13 8 13z" />
      <circle cx="12" cy="9" r="3" />
    </svg>
  );
}

function CalIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21c0-3 3-5 7-5s7 2 7 5" />
      <circle cx="17" cy="9" r="3" />
      <path d="M22 21c0-2-1.5-4-5-4.5" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17L17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <path d="M16 6l-4-4-4 4" />
      <path d="M12 2v13" />
    </svg>
  );
}
